import { Injectable } from '@angular/core';
import { STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';

export type ViewMode = 'dyb' | 'iv';

/**
 * Resolves the view mode (`dyb` / `iv`) requested through the `?view=` query
 * parameter on the initial page load, so an external link can deep-link straight
 * into a specific mode.
 *
 * The parameter is read once and stripped from the address bar (the same
 * approach the `embed` parameter uses in AppComponent.getRetailerId()), but the
 * resolved mode is persisted in session storage so a reload keeps the mode the
 * link asked for - until a saved session (session key) takes over.
 */
@Injectable({
	providedIn: 'root',
})
export class ViewModeService {
	private static readonly QUERY_PARAM = 'view';

	/**
	 * Mode requested via `?view=` on the initial load, or the value persisted for
	 * this tab on a later reload. `null` when nothing was requested.
	 */
	readonly requestedMode: ViewMode | null;

	/**
	 * Mode actually applied for the current session. Set by AppComponent once it
	 * knows there is no incoming session key (a saved session always wins over the
	 * query parameter). Consumed while deciding the initial screen.
	 */
	appliedMode: ViewMode | null = null;

	constructor(private sessionStorageService: SessionStorageService) {
		const params = new URLSearchParams(window.location.search);
		const rawValue = params.get(ViewModeService.QUERY_PARAM);

		// The query parameter wins on the initial navigation; on later reloads it has
		// already been stripped, so fall back to the value persisted for this tab.
		this.requestedMode = this.parseMode(rawValue) ?? this.parseMode(this.readPersistedMode());

		if (this.requestedMode) {
			this.sessionStorageService.setSession(this.requestedMode, STORAGE_NAMES.zip_view_mode);
		}

		if (rawValue !== null) {
			this.stripQueryParam(params);
		}
	}

	/** Forget the deep-link mode once a saved session governs the view instead. */
	clearPersistedMode(): void {
		this.sessionStorageService.removeData(STORAGE_NAMES.zip_view_mode);
	}

	private readPersistedMode(): string | null {
		return this.sessionStorageService.getSession(STORAGE_NAMES.zip_view_mode) ?? null;
	}

	private parseMode(rawValue: string | null): ViewMode | null {
		const value = rawValue?.trim().toLowerCase();

		return value === VIEW_TYPES.design || value === VIEW_TYPES.image_visualisation ? (value as ViewMode) : null;
	}

	private stripQueryParam(params: URLSearchParams): void {
		params.delete(ViewModeService.QUERY_PARAM);

		const query = params.toString();
		const url = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;

		window.history.replaceState(window.history.state, document.title, url);
	}
}
